import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { X, CaretRight, CaretLeft, ChalkboardTeacher, PencilSimple, Trash, UserPlus, ArrowsLeftRight, Student, IdentificationCard } from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { toast } from "sonner";

const EMPTY_TEACHER = { name: "", phone: "", password: "" };

export default function ManageSiswa() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [classes, setClasses] = useState([]);
  const [group, setGroup] = useState(null);
  const [sub, setSub] = useState(isAdmin ? null : user?.kelas || null);

  // teacher modal
  const [tOpen, setTOpen] = useState(false);
  const [tForm, setTForm] = useState(EMPTY_TEACHER);
  const [tEditId, setTEditId] = useState(null);

  // students in sub + move
  const [students, setStudents] = useState([]);
  const [move, setMove] = useState(null); // {kind:'teacher'|'student', item}
  const [moveKelas, setMoveKelas] = useState("");

  const load = () => { api.get("/classes").then((r) => setClasses(r.data)); };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (sub) api.get(`/students?kelas=${encodeURIComponent(sub)}`).then((r) => setStudents(r.data)).catch(() => setStudents([])); else setStudents([]); }, [sub]);

  const allSubs = classes.map((c) => c.kelas);
  const openMove = (kind, item) => { setMove({ kind, item }); setMoveKelas(item.kelas || ""); };
  const doMove = async () => {
    if (!moveKelas || moveKelas === move.item.kelas) { setMove(null); return; }
    try {
      if (move.kind === "teacher") {
        await api.put(`/teachers/${move.item.id}`, { kelas: moveKelas });
      } else {
        const s = move.item;
        await api.put(`/students/${s.id}`, {
          name: s.name, nis: s.nis, student_number: s.student_number || null, kelas: moveKelas,
          gender: s.gender || "Laki-laki", birth_place: s.birth_place || null, birth_date: s.birth_date || null,
          address: s.address || null, father_name: s.father_name || null, mother_name: s.mother_name || null,
          parent_phone: s.parent_phone || null, parent_id: s.parent_id || null, teacher_id: null, avatar: s.avatar || null,
        });
      }
      toast.success("Berhasil dipindahkan");
      setMove(null); load();
      if (sub) api.get(`/students?kelas=${encodeURIComponent(sub)}`).then((r) => setStudents(r.data));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const setT = (k) => (e) => setTForm({ ...tForm, [k]: e.target.value });

  const groupList = [...new Set(classes.map((c) => c.group))];
  const activeGroup = group && groupList.includes(group) ? group : groupList[0];
  const groupSubs = classes.filter((c) => c.group === activeGroup);
  const currentSub = classes.find((c) => c.kelas === sub);

  const selectGroup = (g) => { setGroup(g); setSub(g === "KB" ? "KB" : null); };

  // ---- teacher (Umi Guru) ----
  const openAddTeacher = () => { setTForm(EMPTY_TEACHER); setTEditId(null); setTOpen(true); };
  const openEditTeacher = (t) => { setTForm({ name: t.name, phone: t.phone || "", password: "" }); setTEditId(t.id); setTOpen(true); };
  const submitTeacher = async () => {
    if (!tForm.name.trim()) { toast.error("Nama guru wajib diisi"); return; }
    if (!tForm.phone.trim()) { toast.error("Nomor HP wajib diisi"); return; }
    try {
      if (tEditId) {
        const payload = { name: tForm.name, phone: tForm.phone };
        if (tForm.password) payload.password = tForm.password;
        await api.put(`/teachers/${tEditId}`, payload);
        toast.success("Data Umi Guru diperbarui");
      } else {
        if (!tForm.password.trim()) { toast.error("Password wajib untuk guru baru"); return; }
        await api.post("/teachers", { ...tForm, kelas: sub });
        toast.success("Umi Guru ditambahkan");
      }
      setTOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const removeTeacher = async (id) => {
    if (!window.confirm("Hapus akun Umi Guru ini?")) return;
    await api.delete(`/teachers/${id}`); toast.success("Umi Guru dihapus"); load();
  };

  const showSubList = isAdmin && activeGroup !== "KB" && !sub;

  return (
    <div>
      <PageHeader title="Data Siswa per Kelas" />
      <div className="px-5 pt-4">
        {isAdmin && (
          <button data-testid="import-siswa-btn" onClick={() => navigate("/kelola/import/students")}
            className="w-full mb-3 border-2 border-emerald-500 text-emerald-600 font-semibold py-2.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <UserPlus size={18} weight="bold" /> Import Siswa dari Excel
          </button>
        )}
        {isAdmin && (
          <button data-testid="import-nomor-btn" onClick={() => navigate("/kelola/nomor-siswa")}
            className="w-full mb-4 border-2 border-amber-500 text-amber-600 font-semibold py-2.5 rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-transform">
            <IdentificationCard size={18} weight="bold" /> Isi Nomor Siswa (Excel)
          </button>
        )}
        {/* group tabs (admin only) */}
        {isAdmin && (
          <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
            {groupList.map((g) => {
              const cnt = classes.filter((c) => c.group === g).reduce((a, c) => a + c.student_count, 0);
              return (
                <button key={g} data-testid={`group-tab-${g}`} onClick={() => selectGroup(g)}
                  className={`shrink-0 min-w-[84px] rounded-2xl py-3 px-3 text-center transition-colors ${activeGroup === g ? "bg-primary text-white shadow-lg" : "bg-white border border-stone-200 text-stone-600"}`}>
                  <p className="font-heading font-bold text-sm">{g}</p>
                  <p className={`text-[10px] ${activeGroup === g ? "text-white/80" : "text-stone-400"}`}>{cnt} murid</p>
                </button>
              );
            })}
          </div>
        )}

        {/* sub-class list */}
        {showSubList && (
          <div className="space-y-2.5" data-testid="subclass-list">
            <p className="text-sm text-stone-500 mb-1">Pilih sub-kelas {activeGroup}</p>
            {groupSubs.map((c, i) => (
              <motion.button key={c.kelas} data-testid={`subclass-${c.kelas}`}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                onClick={() => setSub(c.kelas)}
                className="w-full bg-white rounded-2xl border border-stone-200/60 p-4 flex items-center gap-3 shadow-sm active:scale-[0.98] transition-transform">
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <p className="font-heading font-bold text-primary text-sm">{c.kelas.split("-")[1] || c.kelas}</p>
                </div>
                <div className="flex-1 text-left">
                  <p className="font-heading font-semibold text-stone-800">{c.kelas}</p>
                  <p className="text-stone-400 text-xs">{c.student_count} murid • {c.teachers.length} Umi Guru</p>
                </div>
                <CaretRight size={18} className="text-stone-300" />
              </motion.button>
            ))}
          </div>
        )}

        {/* sub-class detail (teachers + students) */}
        {sub && currentSub && (
          <div data-testid="subclass-detail">
            {isAdmin && activeGroup !== "KB" && (
              <button data-testid="back-to-subclasses" onClick={() => setSub(null)} className="flex items-center gap-1 text-primary text-sm font-semibold mb-3">
                <CaretLeft size={16} weight="bold" /> Sub-kelas {activeGroup}
              </button>
            )}
            <h2 className="font-heading text-xl font-bold text-stone-800 mb-3">{sub}</h2>

            {/* Umi Guru pengampu */}
            <div className="bg-white rounded-2xl border border-stone-200/60 p-4 mb-4 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <p className="font-heading font-semibold text-stone-800 flex items-center gap-2">
                  <ChalkboardTeacher size={20} weight="duotone" className="text-primary" /> Umi Guru
                </p>
                {isAdmin && (
                  <button data-testid="add-teacher-btn" onClick={openAddTeacher} className="text-primary text-xs font-semibold flex items-center gap-1">
                    <UserPlus size={16} weight="bold" /> Tambah
                  </button>
                )}
              </div>
              <div className="space-y-2">
                {currentSub.teachers.map((t) => (
                  <div key={t.id} data-testid={`teacher-row-${t.id}`} className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2">
                    <button onClick={() => navigate(`/kelola/guru/${t.id}`)} className="flex items-center gap-2 flex-1 min-w-0 text-left active:scale-[0.98] transition-transform">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-primary text-xs font-bold">{t.name.charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-700 truncate">{t.name}</p>
                        <p className="text-[11px] text-stone-400 truncate">{t.phone || t.email}</p>
                      </div>
                      <CaretRight size={16} className="text-stone-300 shrink-0" />
                    </button>
                    {isAdmin && (
                      <>
                        <button data-testid={`move-teacher-${t.id}`} onClick={() => openMove("teacher", t)} className="text-stone-400 hover:text-amber-500" aria-label="Pindah kelas"><ArrowsLeftRight size={16} /></button>
                        <button data-testid={`edit-teacher-${t.id}`} onClick={() => openEditTeacher(t)} className="text-stone-400 hover:text-primary"><PencilSimple size={16} /></button>
                        <button data-testid={`del-teacher-${t.id}`} onClick={() => removeTeacher(t.id)} className="text-stone-400 hover:text-destructive"><Trash size={16} /></button>
                      </>
                    )}
                  </div>
                ))}
                {currentSub.teachers.length === 0 && <p className="text-stone-400 text-xs text-center py-2">Belum ada Umi Guru. {isAdmin ? "Tekan Tambah." : ""}</p>}
              </div>
            </div>

            {/* Daftar Siswa + pindah kelas */}
            {isAdmin && (
              <div className="bg-white rounded-2xl border border-stone-200/60 p-4 mb-4 shadow-sm">
                <p className="font-heading font-semibold text-stone-800 flex items-center gap-2 mb-2">
                  <Student size={20} weight="duotone" className="text-primary" /> Siswa ({students.length})
                </p>
                <div className="space-y-2">
                  {students.map((s) => (
                    <div key={s.id} data-testid={`student-row-${s.id}`} className="flex items-center gap-2 bg-stone-50 rounded-xl px-3 py-2">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-primary text-xs font-bold">{(s.name || "?").charAt(0)}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-stone-700 truncate">{s.name}</p>
                        <p className="text-[11px] text-stone-400 truncate">NIS {s.nis || "—"}</p>
                      </div>
                      <button data-testid={`move-student-${s.id}`} onClick={() => openMove("student", s)} className="text-stone-400 hover:text-amber-500 shrink-0 flex items-center gap-1 text-xs font-semibold" aria-label="Pindah kelas">
                        <ArrowsLeftRight size={16} /> Pindah
                      </button>
                    </div>
                  ))}
                  {students.length === 0 && <p className="text-stone-400 text-xs text-center py-2">Belum ada siswa di kelas ini.</p>}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* move modal */}
      {move && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setMove(null)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">Pindah {move.kind === "teacher" ? "Umi Guru" : "Siswa"}</h3>
              <button onClick={() => setMove(null)}><X size={22} className="text-stone-400" /></button>
            </div>
            <p className="text-sm text-stone-500 mb-3">Pindahkan <span className="font-semibold text-stone-700">{move.kind === "teacher" ? move.item.name : move.item.name}</span> dari <span className="font-semibold">{move.item.kelas}</span> ke:</p>
            <select data-testid="move-kelas-select" value={moveKelas} onChange={(e) => setMoveKelas(e.target.value)}
              className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 mb-2 outline-none focus:border-primary">
              {allSubs.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            {move.kind === "student" && <p className="text-[11px] text-stone-400 mb-3">Catatan: Umi Guru siswa akan dikosongkan; tetapkan ulang di kelas tujuan bila perlu.</p>}
            <button data-testid="move-submit-btn" onClick={doMove}
              className="w-full bg-primary text-white font-semibold py-3.5 rounded-full active:scale-95 transition-transform mt-2">Pindahkan</button>
          </motion.div>
        </div>
      )}

      {/* teacher modal */}
      {tOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-end md:items-center justify-center" onClick={() => setTOpen(false)}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full md:max-w-md rounded-t-3xl md:rounded-3xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg text-stone-800">{tEditId ? "Edit" : "Tambah"} Umi Guru • {sub}</h3>
              <button onClick={() => setTOpen(false)}><X size={22} className="text-stone-400" /></button>
            </div>
            <div className="space-y-3">
              <Field label="Nama Umi Guru" v={tForm.name} onChange={setT("name")} testid="teacher-name-field" />
              <Field label="Nomor HP (untuk login)" v={tForm.phone} onChange={setT("phone")} testid="teacher-phone-field" />
              <Field label={tEditId ? "Password baru (kosongkan jika tetap)" : "Password"} v={tForm.password} onChange={setT("password")} testid="teacher-password-field" type="password" />
            </div>
            <button data-testid="submit-teacher-btn" onClick={submitTeacher} className="w-full bg-primary text-white font-semibold py-3.5 rounded-full mt-4 active:scale-95 transition-transform">Simpan</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function Field({ label, v, onChange, testid, type = "text" }) {
  return (
    <div>
      <p className="text-xs text-stone-400 mb-1">{label}</p>
      <input data-testid={testid} type={type} value={v} onChange={onChange} className="w-full bg-stone-50 border border-stone-200 rounded-xl py-3 px-3 outline-none focus:border-primary" />
    </div>
  );
}
